import { useEffect, useState, useCallback, useRef } from 'react';
import {
  FileText, Upload, Globe, Building2, FolderOpen, Trash2, RefreshCw,
  CheckCircle, XCircle, Loader2, X, History, MapPin, ChevronDown, ChevronUp, Files, Download, Filter, Pencil
} from 'lucide-react';
import { useIntelligence } from '../contexts';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { IntelligenceDocument, Protocol } from '../types';
import { PageHeader, fieldCls, chipCls, primaryBtnCls } from '../../ui';
import { DOCUMENT_TYPES, isRegistryProjection } from '../types';

interface Site {
  id: string;
  name: string;
  code: string;
}

// Multi-select dropdown component
interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

function MultiSelectDropdown({ label, options, selected, onChange, placeholder = 'All' }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const clearAll = () => onChange([]);

  const displayText = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? options.find(o => o.id === selected[0])?.label || '1 selected'
      : `${selected.length} selected`;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
          selected.length > 0
            ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:border-primary-700 dark:text-primary-400'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
        }`}
      >
        <Filter size={14} />
        <span>{label}: {displayText}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700">
          <div className="p-2 border-b border-gray-100 flex items-center justify-between dark:border-gray-700">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
            {selected.length > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {options.length === 0 ? (
              <p className="text-sm text-gray-500 p-2 dark:text-gray-400">No options available</p>
            ) : (
              options.map(option => (
                <label
                  key={option.id}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.id)}
                    onChange={() => toggleOption(option.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 truncate dark:text-gray-200">{option.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type Scope = 'global' | 'network' | 'site' | 'protocol';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (formData: FormData) => Promise<void>;
  existingDocument?: IntelligenceDocument | null;
  userRole: string;
  currentSiteId: string | null;
  protocols: Protocol[];
}

function UploadModal({ isOpen, onClose, onUpload, existingDocument, userRole, currentSiteId, protocols }: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [scope, setScope] = useState<Scope>(existingDocument?.scope || 'global');
  const [title, setTitle] = useState(existingDocument?.title || '');
  const [description, setDescription] = useState(existingDocument?.description || '');
  const [documentType, setDocumentType] = useState<string>(existingDocument?.document_type || '');
  const [selectedProtocolId, setSelectedProtocolId] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'super_admin' || userRole === 'administrator';

  // Available scopes based on role
  // - Global: super_admin only
  // - Network: admin+ only
  // - Site: all users
  // Protocol-scoped (study) documents upload in eReg from P3 on (plan §5.2) —
  // global/network/site reference docs (SOPs, guidance) stay here until the
  // Phase 5 eReg network zone.
  const availableScopes: { value: Scope; label: string; description: string }[] = [
    ...(isSuperAdmin ? [{ value: 'global' as Scope, label: 'Global', description: 'Available to all users across all sites' }] : []),
    ...(isAdmin ? [{ value: 'network' as Scope, label: 'Network', description: 'Available to all sites in the current network' }] : []),
    { value: 'site' as Scope, label: 'Site', description: 'Available only to the current site' },
  ];

  useEffect(() => {
    if (isOpen && existingDocument) {
      setScope(existingDocument.scope);
      setTitle(existingDocument.title || '');
      setDescription(existingDocument.description || '');
      setDocumentType(existingDocument.document_type || '');
      setSelectedProtocolId(existingDocument.protocol_id || '');
    } else if (isOpen) {
      // Default scope: global for super_admin, site for others
      setScope(isSuperAdmin ? 'global' : 'site');
      setTitle('');
      setDescription('');
      setDocumentType('');
      setSelectedProtocolId(protocols.length > 0 ? protocols[0].id : '');
    }
    setFile(null);
  }, [isOpen, existingDocument, isSuperAdmin, protocols]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    if (scope === 'protocol' && !selectedProtocolId) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('scope', scope);
      formData.append('title', title || file.name);
      if (description) formData.append('description', description);
      if (documentType) formData.append('document_type', documentType);
      if (currentSiteId) formData.append('site_id', currentSiteId);
      if (scope === 'protocol' && selectedProtocolId) {
        formData.append('protocol_id', selectedProtocolId);
      }
      if (existingDocument) {
        formData.append('document_group_id', existingDocument.document_group_id);
      }

      await onUpload(formData);
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col dark:bg-gray-800">
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {existingDocument ? 'Upload New Version' : 'Upload Document'}
          </h2>
          <button onClick={onClose} className="p-1 rounded text-gray-500 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">
          {existingDocument && (
            <div className="bg-primary-50 text-primary-700 p-3 rounded-lg text-sm dark:bg-primary-900/20 dark:text-primary-300">
              Uploading new version of: <strong>{existingDocument.title || existingDocument.filename}</strong>
              <br />
              Current version: {existingDocument.version}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">File</label>
            <input
              type="file"
              accept=".pdf,.txt,.md"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className={`w-full ${fieldCls}`}
              required
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Supported: PDF, TXT, Markdown</p>
          </div>

          {!existingDocument && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Scope</label>
              <div className="space-y-2">
                {availableScopes.map((s) => (
                  <label
                    key={s.value}
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      scope === s.value ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="scope"
                      value={s.value}
                      checked={scope === s.value}
                      onChange={(e) => setScope(e.target.value as Scope)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{s.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{s.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Protocol Selection (when protocol scope is selected) */}
          {scope === 'protocol' && !existingDocument && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Protocol</label>
              <select
                value={selectedProtocolId}
                onChange={(e) => setSelectedProtocolId(e.target.value)}
                className={`w-full ${fieldCls}`}
                required
              >
                <option value="">Select a protocol...</option>
                {protocols.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.protocol_number} - {p.study_nickname || 'Untitled'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Document Type</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className={`w-full ${fieldCls}`}
            >
              <option value="">Select document type...</option>
              {DOCUMENT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the document"
              rows={2}
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!file || uploading || (scope === 'protocol' && !selectedProtocolId)}
              className="flex-1 px-4 py-2 bg-primary-600 text-sm font-medium text-white rounded-lg transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  {existingDocument ? 'Upload Version' : 'Upload'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: IntelligenceDocument | null;
}

function VersionHistoryModal({ isOpen, onClose, document }: VersionHistoryModalProps) {
  const [versions, setVersions] = useState<IntelligenceDocument[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && document) {
      loadVersions();
    }
  }, [isOpen, document]);

  const loadVersions = async () => {
    if (!document) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/intelligence/documents/${document.id}/versions`);
      setVersions(response.data || []);
    } catch (error) {
      console.error('Error loading versions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col dark:bg-gray-800">
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Version History</h2>
          <button onClick={onClose} className="p-1 rounded text-gray-500 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">{document.title || document.filename}</p>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-primary-600 dark:text-primary-400" size={24} />
            </div>
          ) : (
            <div className="space-y-2">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className={`p-3 rounded-lg border ${
                    v.is_current ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">Version {v.version}</span>
                      {v.is_current && (
                        <span className="text-xs bg-primary-600 text-white px-2 py-0.5 rounded-full">Current</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(v.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">{v.filename}</p>
                  {v.replaced_at && (
                    <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                      Replaced on {new Date(v.replaced_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface BatchUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
  userRole: string;
  currentSiteId: string | null;
  protocols: Protocol[];
}

function BatchUploadModal({ isOpen, onClose, onUploadComplete, userRole, currentSiteId, protocols }: BatchUploadModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [scope, setScope] = useState<Scope>('site');
  const [selectedProtocolId, setSelectedProtocolId] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ uploaded: number; total: number; errors: string[] }>({ uploaded: 0, total: 0, errors: [] });
  const [dragOver, setDragOver] = useState(false);

  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'super_admin' || userRole === 'administrator';

  // Available scopes based on role (study/protocol docs upload in eReg — P3)
  const availableScopes: { value: Scope; label: string }[] = [
    ...(isSuperAdmin ? [{ value: 'global' as Scope, label: 'Global' }] : []),
    ...(isAdmin ? [{ value: 'network' as Scope, label: 'Network' }] : []),
    { value: 'site' as Scope, label: 'Site' },
  ];

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setScope(isSuperAdmin ? 'global' : 'site');
      setSelectedProtocolId(protocols.length > 0 ? protocols[0].id : '');
      setUploadProgress({ uploaded: 0, total: 0, errors: [] });
    }
  }, [isOpen, isSuperAdmin, protocols]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
      f.type === 'application/pdf' || f.type === 'text/plain' || f.name.endsWith('.md')
    );
    setFiles(prev => [...prev, ...droppedFiles]);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...selectedFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) return;
    if (scope === 'protocol' && !selectedProtocolId) return;

    setUploading(true);
    setUploadProgress({ uploaded: 0, total: files.length, errors: [] });

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('scope', scope);
      if (scope === 'protocol' && selectedProtocolId) {
        formData.append('protocol_id', selectedProtocolId);
      }

      const response = await api.post('/api/intelligence/documents/batch', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { uploaded, errors } = response.data;
      setUploadProgress({
        uploaded: uploaded?.length || 0,
        total: files.length,
        errors: errors?.map((e: { filename: string; error: string }) => `${e.filename}: ${e.error}`) || [],
      });

      if (uploaded?.length > 0) {
        onUploadComplete();
      }

      // Close after a short delay if successful
      if (!errors?.length) {
        setTimeout(() => onClose(), 1500);
      }
    } catch (error: any) {
      console.error('Batch upload error:', error);
      setUploadProgress(prev => ({
        ...prev,
        errors: [error.response?.data?.error || 'Upload failed. Please try again.'],
      }));
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col dark:bg-gray-800">
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 dark:text-gray-100">
            <Files size={20} />
            Batch Upload Documents
          </h2>
          <button onClick={onClose} className="p-1 rounded text-gray-500 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Scope Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">Upload to</label>
            <div className="flex flex-wrap gap-2">
              {availableScopes.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setScope(s.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    scope === s.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Protocol Selection (if protocol scope) */}
          {scope === 'protocol' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Protocol</label>
              <select
                value={selectedProtocolId}
                onChange={(e) => setSelectedProtocolId(e.target.value)}
                className={`w-full ${fieldCls}`}
                required
              >
                <option value="">Select a protocol...</option>
                {protocols.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.protocol_number} - {p.study_nickname || 'Untitled'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragOver
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
            }`}
          >
            <Upload size={32} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 mb-2 dark:text-gray-400">Drag and drop files here, or</p>
            <label className="inline-block">
              <input
                type="file"
                accept=".pdf,.txt,.md"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <span className="px-4 py-2 bg-primary-600 text-sm font-medium text-white rounded-lg transition-colors duration-150 hover:bg-primary-700 cursor-pointer">
                Browse Files
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-3 dark:text-gray-400">Supported: PDF, TXT, Markdown (up to 20 files)</p>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {files.length} file{files.length !== 1 ? 's' : ''} selected
                </label>
                <button
                  type="button"
                  onClick={() => setFiles([])}
                  className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Clear all
                </button>
              </div>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-48 overflow-y-auto dark:border-gray-700 dark:divide-gray-700">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-900 truncate dark:text-gray-100">{file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{formatFileSize(file.size)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 text-gray-400 transition-colors duration-150 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploadProgress.total > 0 && (
            <div className={`p-4 rounded-lg ${uploadProgress.errors.length > 0 ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
              <div className="flex items-center gap-2 mb-2">
                {uploading ? (
                  <Loader2 size={16} className="animate-spin text-primary-600 dark:text-primary-400" />
                ) : uploadProgress.errors.length > 0 ? (
                  <XCircle size={16} className="text-yellow-600" />
                ) : (
                  <CheckCircle size={16} className="text-green-600" />
                )}
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {uploading
                    ? 'Uploading...'
                    : `${uploadProgress.uploaded} of ${uploadProgress.total} files uploaded`}
                </span>
              </div>
              {uploadProgress.errors.length > 0 && (
                <div className="text-sm text-yellow-700 space-y-1 dark:text-yellow-400">
                  {uploadProgress.errors.map((err, i) => (
                    <p key={i}>{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={files.length === 0 || uploading || (scope === 'protocol' && !selectedProtocolId)}
              className="flex-1 px-4 py-2 bg-primary-600 text-sm font-medium text-white rounded-lg transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload {files.length} File{files.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function Documents() {
  const { protocols } = useIntelligence();
  const { currentSite, user } = useAuth();
  const [documents, setDocuments] = useState<IntelligenceDocument[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | Scope>('all');
  const [selectedProtocols, setSelectedProtocols] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [batchUploadModalOpen, setBatchUploadModalOpen] = useState(false);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<IntelligenceDocument | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const userRole = user?.role || 'basic_user';
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = ['super_admin', 'administrator'].includes(userRole);

  useEffect(() => {
    loadDocuments();
    loadSites();
  }, [currentSite]);

  const loadSites = async () => {
    try {
      const response = await api.get('/api/sites');
      setSites(response.data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/intelligence/documents');
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (formData: FormData) => {
    await api.post('/api/intelligence/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    await loadDocuments();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this document? All associated chunks will be removed.')) {
      return;
    }

    try {
      await api.delete(`/api/intelligence/documents/${id}`);
      await loadDocuments();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      alert(error.response?.data?.error || 'Failed to delete document. Please try again.');
    }
  };

  const handleRename = async (doc: IntelligenceDocument) => {
    const current = doc.title || doc.filename;
    const next = window.prompt('Rename document', current);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;

    try {
      await api.patch(`/api/intelligence/documents/${doc.id}`, { title: trimmed });
      await loadDocuments();
    } catch (error: any) {
      console.error('Error renaming document:', error);
      alert(error.response?.data?.error || 'Failed to rename document.');
    }
  };

  const handleReprocess = async (id: string) => {
    try {
      await api.post(`/api/intelligence/documents/${id}/reprocess`);
      await loadDocuments();
    } catch (error) {
      console.error('Error reprocessing document:', error);
      alert('Failed to reprocess document. Please try again.');
    }
  };

  const handleDownload = async (doc: IntelligenceDocument) => {
    try {
      const response = await api.get(`/api/intelligence/documents/${doc.id}/download`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading document:', error);
      alert(error.response?.data?.error || 'Failed to download document.');
    }
  };

  const openUploadNewVersion = (doc: IntelligenceDocument) => {
    setSelectedDocument(doc);
    setUploadModalOpen(true);
  };

  const openVersionHistory = (doc: IntelligenceDocument) => {
    setSelectedDocument(doc);
    setVersionModalOpen(true);
  };

  const getScopeIcon = (scope: string) => {
    switch (scope) {
      case 'global': return <Globe size={16} className="text-blue-600" />;
      case 'network': return <Building2 size={16} className="text-green-600" />;
      case 'site': return <MapPin size={16} className="text-orange-600" />;
      case 'protocol': return <FolderOpen size={16} className="text-primary-600" />;
      default: return <FileText size={16} className="text-gray-600" />;
    }
  };

  const getScopeLabel = (scope: string) => {
    switch (scope) {
      case 'global': return 'Global Documents';
      case 'network': return 'Network Documents';
      case 'site': return 'Site Documents';
      case 'protocol': return 'Protocol Documents';
      default: return 'Documents';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            <CheckCircle size={12} />
            Processed
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            <Loader2 size={12} className="animate-spin" />
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
            <XCircle size={12} />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            Pending
          </span>
        );
    }
  };

  const canManageDocument = (doc: IntelligenceDocument) => {
    // Registry projections mirror eReg — read-only here, managed in eReg (P3).
    if (isRegistryProjection(doc)) return false;
    if (isSuperAdmin) return true;
    if (doc.scope === 'global') return false; // Only super_admin can manage global
    if (doc.scope === 'network' && !isAdmin) return false; // Only admin+ can manage network
    // For site/protocol scope, all users with access can manage
    return true;
  };

  const filteredDocuments = documents.filter(doc => {
    // Scope filter
    if (filter !== 'all' && doc.scope !== filter) return false;

    // Protocol filter (multi-select)
    if (selectedProtocols.length > 0 && !selectedProtocols.includes(doc.protocol_id || '')) {
      return false;
    }

    // Site filter (multi-select)
    if (selectedSites.length > 0 && !selectedSites.includes(doc.site_id || '')) {
      return false;
    }

    return true;
  });

  // Build filter options
  const protocolOptions: MultiSelectOption[] = protocols.map(p => ({
    id: p.id,
    label: `${p.protocol_number}${p.study_nickname ? ` - ${p.study_nickname}` : ''}`,
  }));

  const siteOptions: MultiSelectOption[] = sites.map(s => ({
    id: s.id,
    label: s.name || s.code,
  }));

  // Group documents by scope
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    if (!acc[doc.scope]) acc[doc.scope] = [];
    acc[doc.scope].push(doc);
    return acc;
  }, {} as Record<string, IntelligenceDocument[]>);

  const scopeOrder: Scope[] = ['global', 'network', 'site', 'protocol'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Upload buttons - scope-based access control handled by backend */}
      <PageHeader
        className="mb-6"
        title="Document Library"
        icon={FileText}
        subtitle="Manage documents for AI-powered Q&A"
        actions={
          <>
            <button onClick={() => setBatchUploadModalOpen(true)} className={primaryBtnCls}>
              <Files size={16} />
              Batch Upload
            </button>
            <button
              onClick={() => {
                setSelectedDocument(null);
                setUploadModalOpen(true);
              }}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
            >
              <Upload size={16} />
              Single Upload
            </button>
          </>
        }
      />

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap items-center">
        {/* Scope filter */}
        {(['all', 'global', 'network', 'site', 'protocol'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={chipCls(filter === f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}

        <div className="w-px h-6 bg-gray-300 mx-2 dark:bg-gray-600" />

        {/* Protocol filter */}
        <MultiSelectDropdown
          label="Protocol"
          options={protocolOptions}
          selected={selectedProtocols}
          onChange={setSelectedProtocols}
        />

        {/* Site filter */}
        <MultiSelectDropdown
          label="Site"
          options={siteOptions}
          selected={selectedSites}
          onChange={setSelectedSites}
        />

        {/* Clear filters */}
        {(selectedProtocols.length > 0 || selectedSites.length > 0) && (
          <button
            onClick={() => {
              setSelectedProtocols([]);
              setSelectedSites([]);
            }}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Document list */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <FileText size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No documents yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Upload documents to enable AI-powered Q&A</p>
        </div>
      ) : (
        <div className="space-y-6">
          {scopeOrder.map((scope) => {
            const docs = groupedDocuments[scope] || [];
            if (docs.length === 0) return null;

            return (
              <div key={scope}>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2 dark:text-gray-400">
                  {getScopeIcon(scope)}
                  {getScopeLabel(scope)}
                  <span className="text-gray-400 font-normal dark:text-gray-500">({docs.length})</span>
                </h2>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Document</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Version</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Chunks</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Uploaded</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {docs.map((doc) => (
                        <tr key={doc.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <FileText size={20} className="text-gray-400" />
                              <div>
                                <p className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                                  {doc.title || doc.filename}
                                  {isRegistryProjection(doc) && (
                                    <span
                                      className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                                      title="Managed in eReg — this row mirrors the registry document"
                                    >
                                      eReg
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{doc.filename}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openVersionHistory(doc)}
                              className="inline-flex items-center gap-1 text-sm text-gray-600 transition-colors duration-150 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400"
                            >
                              v{doc.version}
                              <History size={14} />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(doc.processing_status)}
                            {doc.processing_error && (
                              <p className="text-xs text-red-600 mt-1 max-w-xs truncate dark:text-red-400" title={doc.processing_error}>
                                {doc.processing_error}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {doc.chunk_count > 0 ? `${doc.chunk_count} chunks` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleDownload(doc)}
                                className="p-1 text-gray-400 transition-colors duration-150 hover:text-green-600 dark:hover:text-green-400"
                                title="Download"
                              >
                                <Download size={16} />
                              </button>
                              {canManageDocument(doc) && (
                                <>
                                  <button
                                    onClick={() => handleRename(doc)}
                                    className="p-1 text-gray-400 transition-colors duration-150 hover:text-primary-600 dark:hover:text-primary-400"
                                    title="Rename document"
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  {doc.scope !== 'protocol' && (
                                    // Study-doc versions upload in eReg (P3) —
                                    // legacy protocol rows migrate via Registry adoption.
                                    <button
                                      onClick={() => openUploadNewVersion(doc)}
                                      className="p-1 text-gray-400 transition-colors duration-150 hover:text-primary-600 dark:hover:text-primary-400"
                                      title="Upload new version"
                                    >
                                      <Upload size={16} />
                                    </button>
                                  )}
                                  {doc.processing_status === 'failed' && (
                                    <button
                                      onClick={() => handleReprocess(doc.id)}
                                      className="p-1 text-gray-400 transition-colors duration-150 hover:text-blue-600 dark:hover:text-blue-400"
                                      title="Reprocess"
                                    >
                                      <RefreshCw size={16} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDelete(doc.id)}
                                    className="p-1 text-gray-400 transition-colors duration-150 hover:text-red-600 dark:hover:text-red-400"
                                    title="Delete"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setSelectedDocument(null);
        }}
        onUpload={handleUpload}
        existingDocument={selectedDocument}
        userRole={userRole}
        currentSiteId={currentSite?.id || null}
        protocols={protocols}
      />

      <VersionHistoryModal
        isOpen={versionModalOpen}
        onClose={() => {
          setVersionModalOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
      />

      <BatchUploadModal
        isOpen={batchUploadModalOpen}
        onClose={() => setBatchUploadModalOpen(false)}
        onUploadComplete={loadDocuments}
        userRole={userRole}
        currentSiteId={currentSite?.id || null}
        protocols={protocols}
      />
    </div>
  );
}
